import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Webcam from 'react-webcam';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Paper,
} from '@material-ui/core';
import {
  CameraAlt as CameraIcon,
  Crop as CropIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

// Utility to convert dataURL to File
function dataURLtoFile(dataurl, filename) {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

// Utility to convert blob URL to File (CSP-safe)
function blobUrlToFile(blobUrl, filename) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', blobUrl, true);
    xhr.responseType = 'blob';
    xhr.onload = function () {
      if (xhr.status === 200) {
        resolve(new File([xhr.response], filename, { type: xhr.response.type }));
      } else {
        reject(new Error('Failed to fetch blob'));
      }
    };
    xhr.onerror = reject;
    xhr.send();
  });
}

const WebcamCapture = ({
  onCapture,
  onClose,
  open,
  aspectRatio = 1,
  enableCrop = true,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.8,
}) => {
  const { t } = useTranslation();
  const webcamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [crop, setCrop] = useState();
  const [initialCrop, setInitialCrop] = useState();
  const [hasUserCropped, setHasUserCropped] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [croppedImage, setCroppedImage] = useState(null);
  const [croppedBlob, setCroppedBlob] = useState(null);
  const imageRef = useRef(null);

  const videoConstraints = {
    width: maxWidth,
    height: maxHeight,
    facingMode: 'user',
  };

  const capture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setCapturedImage(imageSrc);
    }
  }, [webcamRef]);

  const retake = useCallback(() => {
    setCapturedImage(null);
    setCroppedImage(null);
    setIsCropping(false);
  }, []);

  const startCrop = useCallback(() => {
    setIsCropping(true);
  }, []);

  const onCropImageLoaded = useCallback((img) => {
    imageRef.current = img;
    if (!crop) {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      let cropObj;
      if (aspectRatio && aspectRatio !== 0) {
        const imgAspect = width / height;
        if (imgAspect > aspectRatio) {
          const cropWidth = height * aspectRatio;
          cropObj = {
            unit: 'px',
            x: Math.round((width - cropWidth) / 2),
            y: 0,
            width: Math.round(cropWidth),
            height: height,
            aspect: aspectRatio,
          };
        } else {
          const cropHeight = width / aspectRatio;
          cropObj = {
            unit: 'px',
            x: 0,
            y: Math.round((height - cropHeight) / 2),
            width: width,
            height: Math.round(cropHeight),
            aspect: aspectRatio,
          };
        }
      } else {
        cropObj = {
          unit: 'px',
          x: 0,
          y: 0,
          width: width,
          height: height,
        };
      }
      setCrop(cropObj);
      setInitialCrop(cropObj);
      setHasUserCropped(false);
      return false;
    }
    return false;
  }, [aspectRatio, crop]);

  const getCroppedImg = useCallback(
    (imageSrc, crop) => {
      return new Promise((resolve) => {
        const image = new Image();
        image.src = imageSrc;
        image.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            resolve(new File([], 'captured-image.jpg'));
            return;
          }

          const scaleX = image.naturalWidth / image.width;
          const scaleY = image.naturalHeight / image.height;

          canvas.width = crop.width * scaleX;
          canvas.height = crop.height * scaleY;

          ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width * scaleX,
            crop.height * scaleY
          );

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const file = new File([blob], 'captured-image.jpg', {
                  type: 'image/jpeg',
                });
                resolve(file);
              } else {
                resolve(new File([], 'captured-image.jpg'));
              }
            },
            'image/jpeg',
            quality
          );
        };
      });
    },
    [quality]
  );

  const applyCrop = useCallback(async () => {
    if (capturedImage && crop && imageRef.current) {
      const croppedFile = await getCroppedImg(capturedImage, crop);
      setCroppedImage(URL.createObjectURL(croppedFile));
      setCroppedBlob(croppedFile);
      setIsCropping(false);
    }
  }, [capturedImage, crop, getCroppedImg]);

  const confirmCapture = useCallback(async () => {
    let file;

    // Helper to check if a string is a data URL
    const isDataURL = (url) => url && url.startsWith('data:');
    // Helper to check if a string is a blob URL
    const isBlobURL = (url) => url && url.startsWith('blob:');

    if (croppedImage && enableCrop) {
      if (isDataURL(croppedImage)) {
        file = dataURLtoFile(croppedImage, 'captured-image.jpg');
      } else if (isBlobURL(croppedImage) && croppedBlob) {
        file = new File([croppedBlob], 'captured-image.jpg', { type: croppedBlob.type || 'image/jpeg' });
      }
    } else if (capturedImage) {
      file = dataURLtoFile(capturedImage, 'captured-image.jpg');
    } else {
      return;
    }

    onCapture(file);
    onClose();
    retake();
  }, [capturedImage, croppedImage, enableCrop, onCapture, onClose, retake]);

  const handleClose = useCallback(() => {
    onClose();
    retake();
  }, [onClose, retake]);

  // Helper to compare two crop objects
  const isCropEqual = (a, b) => {
    if (!a || !b) return false;
    return (
      a.unit === b.unit &&
      a.x === b.x &&
      a.y === b.y &&
      a.width === b.width &&
      a.height === b.height &&
      a.aspect === b.aspect
    );
  };

  // When crop changes, check if user has changed it from initial
  const handleCropChange = (newCrop) => {
    setCrop(newCrop);
  };

  // Handler for user interaction with crop
  const handleCropUserInteraction = () => {
    if (!hasUserCropped) setHasUserCropped(true);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: '#f5f5f5',
          borderRadius: 12,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t('WEBCAM_CAPTURE_TITLE')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 16 }}>
          {!capturedImage ? (
            // Webcam view
            <Paper elevation={3} style={{ padding: 16, borderRadius: 8 }}>
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                style={{
                  width: '100%',
                  maxWidth: 640,
                  height: 'auto',
                  borderRadius: 8,
                }}
              />
              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CameraIcon />}
                  onClick={capture}
                  size="large"
                >
                  {t('WEBCAM_CAPTURE_BUTTON')}
                </Button>
              </Box>
            </Paper>
          ) : !isCropping ? (
            // Captured image view
            <Paper elevation={3} style={{ padding: 16, borderRadius: 8 }}>
              <img
                src={capturedImage}
                alt="Captured"
                style={{
                  width: '100%',
                  maxWidth: 640,
                  height: 'auto',
                  borderRadius: 8,
                }}
              />
              <Box mt={2} display="flex" justifyContent="center" style={{ gap: 8 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={retake}
                >
                  {t('WEBCAM_RETAKE_BUTTON')}
                </Button>
                {enableCrop && (
                  <Button
                    variant="outlined"
                    startIcon={<CropIcon />}
                    onClick={startCrop}
                  >
                    {t('WEBCAM_CROP_BUTTON')}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckIcon />}
                  onClick={confirmCapture}
                >
                  {t('WEBCAM_USE_PHOTO_BUTTON')}
                </Button>
              </Box>
            </Paper>
          ) : (
            // Cropping view
            <Paper elevation={3} style={{ padding: 16, borderRadius: 8 }}>
              <ReactCrop
                crop={crop}
                onChange={handleCropChange}
                aspect={aspectRatio}
                circularCrop={false}
                onImageLoaded={onCropImageLoaded}
                onDragStart={handleCropUserInteraction}
                onComplete={handleCropUserInteraction}
              >
                <img
                  ref={imageRef}
                  src={capturedImage}
                  alt="Crop"
                  style={{
                    width: '100%',
                    maxWidth: 640,
                    height: 'auto',
                    borderRadius: 8,
                  }}
                />
              </ReactCrop>
              <Box mt={2} display="flex" justifyContent="center" style={{ gap: 8 }}>
                <Button
                  variant="outlined"
                  onClick={() => setIsCropping(false)}
                >
                  {t('WEBCAM_CANCEL_CROP_BUTTON')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={applyCrop}
                  disabled={!hasUserCropped}
                >
                  {t('WEBCAM_APPLY_CROP_BUTTON')}
                </Button>
              </Box>
            </Paper>
          )}

          {croppedImage && !isCropping && (
            <Paper elevation={2} style={{ padding: 8, borderRadius: 4 }}>
              <Typography variant="caption" color="textSecondary">
                {t('WEBCAM_CROPPED_PREVIEW')}
              </Typography>
              <img
                src={croppedImage}
                alt="Cropped"
                style={{
                  width: 200,
                  height: 200,
                  objectFit: 'cover',
                  borderRadius: 4,
                  marginTop: 4,
                }}
              />
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {t('WEBCAM_CANCEL_BUTTON')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WebcamCapture.propTypes = {
  onCapture: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  aspectRatio: PropTypes.number,
  enableCrop: PropTypes.bool,
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  quality: PropTypes.number,
};

export default WebcamCapture; 